import React, { useState, useEffect } from 'react';
import { Mail, Phone, MapPin, Clock, Send, CheckCircle } from 'lucide-react';
import { teamMembers } from '../data/mock';
import axios from 'axios';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    organization: '',
    email: '',
    country: '',
    projectType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const API = `${BACKEND_URL}/api`;

      await axios.post(`${API}/contact`, {
        name: formData.name,
        organization: formData.organization,
        email: formData.email,
        country: formData.country,
        project_type: formData.projectType,
        message: formData.message
      });

      setIsSubmitting(false);
      setSubmitSuccess(true);
      setFormData({ name: '', organization: '', email: '', country: '', projectType: '', message: '' });
      setTimeout(() => setSubmitSuccess(false), 5000);
    } catch (error) {
      console.error('Contact form error:', error);
      setIsSubmitting(false);
      alert('There was an error submitting your message. Please try again or contact us directly at marketing@creduce.org');
    }
  };

  const inputClass = 'w-full px-4 py-3 bg-white border border-zinc-300 text-[#09090B] focus:outline-none focus:ring-1 focus:ring-[#047857] focus:border-[#047857] placeholder:text-zinc-400 transition-colors';
  const labelClass = 'block text-sm font-semibold text-[#09090B] mb-2';

  return (
    <div className="contact-page bg-white" data-testid="contact-page">
      {/* Hero */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-20 bg-[#FAFAFA] border-b border-zinc-200">
        <div className="container mx-auto">
          <div className="max-w-3xl">
            <div className="accent-line mb-6" />
            <span className="overline">Get In Touch</span>
            <h1 className="text-[#09090B] mt-4 mb-6">Let's Create Impact Together</h1>
            <p className="text-lg md:text-xl text-[#52525B] leading-relaxed">
              Ready to start your sustainability journey? Connect with our team of experts.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Form */}
            <div className="border border-zinc-200 p-8 md:p-10">
              {submitSuccess ? (
                <div className="text-center py-12" data-testid="contact-success">
                  <div className="w-20 h-20 bg-[#047857] flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-[#09090B] mb-4">Thank You!</h3>
                  <p className="text-[#52525B]">
                    Your message has been sent. We'll get back to you within 24 hours.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className={labelClass}>Name *</label>
                      <input type="text" name="name" value={formData.name} onChange={handleChange} required className={inputClass} placeholder="Your name" data-testid="contact-input-name" />
                    </div>
                    <div>
                      <label className={labelClass}>Organization</label>
                      <input type="text" name="organization" value={formData.organization} onChange={handleChange} className={inputClass} placeholder="Company name" data-testid="contact-input-organization" />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className={labelClass}>Email *</label>
                      <input type="email" name="email" value={formData.email} onChange={handleChange} required className={inputClass} placeholder="your@email.com" data-testid="contact-input-email" />
                    </div>
                    <div>
                      <label className={labelClass}>Country</label>
                      <input type="text" name="country" value={formData.country} onChange={handleChange} className={inputClass} placeholder="Your country" data-testid="contact-input-country" />
                    </div>
                  </div>

                  <div>
                    <label className={labelClass}>Project Type</label>
                    <select name="projectType" value={formData.projectType} onChange={handleChange} className={inputClass} data-testid="contact-select-project-type">
                      <option value="">Select project type</option>
                      <option value="carbon-credits">Carbon Credits Development</option>
                      <option value="net-zero">Net Zero / Carbon Neutrality</option>
                      <option value="esg">ESG Reporting</option>
                      <option value="nature-based">Nature-Based Solutions</option>
                      <option value="trading">Carbon Trading</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className={labelClass}>Message *</label>
                    <textarea name="message" value={formData.message} onChange={handleChange} required rows={5} className={`${inputClass} resize-none`} placeholder="Tell us about your project or inquiry..." data-testid="contact-input-message" />
                  </div>

                  <button type="submit" disabled={isSubmitting} className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed" data-testid="contact-submit-btn">
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <span>Send Message</span>
                        <Send className="ml-2 w-4 h-4" />
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>

            {/* Info */}
            <div className="space-y-6">
              {teamMembers.map((member, index) => (
                <div key={index} className="border border-zinc-200 p-6" data-testid={`contact-member-${index}`}>
                  <h4 className="font-bold text-[#09090B] mb-1 text-lg">{member.name}</h4>
                  <p className="text-sm text-[#52525B] mb-4">{member.role}</p>
                  <div className="space-y-2">
                    <a href={`mailto:${member.email}`} className="flex items-center gap-2 text-sm text-[#52525B] hover:text-[#047857] transition-colors">
                      <Mail className="w-4 h-4" />
                      {member.email}
                    </a>
                    <a href={`tel:${member.phone}`} className="flex items-center gap-2 text-sm text-[#52525B] hover:text-[#047857] transition-colors">
                      <Phone className="w-4 h-4" />
                      {member.phone}
                    </a>
                  </div>
                </div>
              ))}

              {/* Office Hours */}
              <div className="bg-[#047857] text-white p-6">
                <Clock className="w-8 h-8 mb-4" />
                <h4 className="font-bold mb-3 text-lg">Office Hours</h4>
                <p className="text-sm text-white/90 mb-4">
                  Monday - Friday: 9:00 AM - 6:00 PM IST<br />
                  Saturday: 9:00 AM - 1:00 PM IST
                </p>
                <p className="text-sm text-white/90">
                  We typically respond within 24 hours
                </p>
              </div>

              {/* Headquarters */}
              <div className="border border-zinc-200 p-6">
                <MapPin className="w-8 h-8 text-[#047857] mb-4" />
                <h4 className="font-bold text-[#09090B] mb-3 text-lg">Headquarters</h4>
                <p className="text-sm text-[#52525B]">
                  Ahmedabad, Gujarat, India
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
